import React, { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import { useDidUpdate } from 'utils/hooks'

import { SEND_SIMULATIONS_ASYNC } from 'containers/SpearPhishingAgent/constants'
import {
  selectCampaignsData,
  selectExcludedParticipants,
  selectLaunchLoader,
  selectSingleCampaignId,
} from 'containers/SpearPhishingAgent/selectors'
import { getActiveSelectedCampaignIds } from 'containers/SpearPhishingAgent/utils'

import { Language, Rocket, SmsTracking, Users } from 'components/lib/DesignSystem/Icons'
import Modal from 'components/lib/DesignSystem/Modal'
import Button, { SIZES, VARIANTS } from 'components/lib/DesignSystem/V3/Button'
import { PSIMA_PENDO_FEATURES } from 'components/pages/SpearPhishingAgent/psimaPendo'

const ModalTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--color-text);
  font-weight: var(--t-typography-font-weight-medium);
  font-size: var(--t-typography-font-size-base);
  line-height: var(--t-typography-line-height-base);
`

const FooterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`

const StatsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 50px;
  width: 100%;
  height: 74px;
  padding: 14px 62px;
  box-sizing: border-box;
  background: var(--psima-quick-launch-modal-box-bg);
  border: 1px solid var(--psima-quick-launch-modal-box-border);
  border-radius: 10px;
`

const StatsItem = styled.div`
  display: flex;
  flex: 1 0 0;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-width: 0;
`

const IconBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 8px;
  border-radius: 10px;
  background: var(--psima-quick-launch-modal-icon-bg);
`

const StatsText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  white-space: nowrap;
`

const StatsCount = styled.div`
  font-size: var(--t-typography-font-size-lg);
  line-height: var(--t-typography-line-height-lg);
  font-weight: var(--t-typography-font-weight-medium);
  color: var(--color-text);
`

const StatsLabel = styled.div`
  font-size: var(--t-typography-font-size-xs);
  line-height: var(--t-typography-line-height-xs);
  font-weight: var(--t-typography-font-weight-regular);
  color: var(--sub-text-color);
`

const Description = styled.div`
  font-size: var(--t-typography-font-size-sm);
  line-height: var(--t-typography-line-height-sm);
  font-weight: var(--t-typography-font-weight-regular);
  color: var(--color-text);
  text-align: center;
`

const InfoRow = styled(StatsContainer)`
  height: auto;
  gap: 10px;
  padding: 14px;
  align-items: flex-start;
  justify-content: flex-start;
`

const InfoTitle = styled(Description)`
  text-align: left;
  font-weight: var(--t-typography-font-weight-medium);
`

const QuickLaunchModal = ({ isQuickLaunchModalOpen, setIsQuickLaunchModalOpen }) => {
  const dispatch = useDispatch()
  const campaignsData = useSelector(selectCampaignsData)
  const launchLoader = useSelector(selectLaunchLoader)
  const excludedParticipants = useSelector(selectExcludedParticipants)
  const singleCampaignId = useSelector(selectSingleCampaignId)
  const campaignsCount = singleCampaignId
    ? 1
    : getActiveSelectedCampaignIds(
        campaignsData.items,
        campaignsData.selectedCampaigns,
        excludedParticipants
      ).length
  const participantsCount = useMemo(() => {
    if (!isQuickLaunchModalOpen) return 0

    if (singleCampaignId) {
      const campaign = campaignsData.items.find(
        ({ id: campaignId }) => campaignId === singleCampaignId
      )

      return (
        campaign.audience.suggestedProfiles.length -
        (excludedParticipants[singleCampaignId]?.length || 0)
      )
    } else {
      return campaignsData.selectedCampaigns.reduce((acc, campaignId) => {
        const campaign = campaignsData.items.find(({ id: itemId }) => itemId === campaignId)
        if (!campaign) return acc

        return (
          acc +
          campaign.audience.suggestedProfiles.length -
          (excludedParticipants[campaignId]?.length || 0)
        )
      }, 0)
    }
  }, [
    singleCampaignId,
    campaignsData.items,
    campaignsData.selectedCampaigns,
    excludedParticipants,
    isQuickLaunchModalOpen,
  ])

  const onGenerateClick = () => {
    dispatch({
      type: SEND_SIMULATIONS_ASYNC.INITIATE,
    })
  }

  useDidUpdate(() => {
    if (!launchLoader) {
      setIsQuickLaunchModalOpen(false)
    }
  }, [launchLoader])

  return (
    <Modal
      id="Psima_Dashboard_QuickLaunch_Modal"
      open={isQuickLaunchModalOpen}
      title={
        <ModalTitle>
          <Rocket width="24px" height="24px" fill="var(--color-text)" />
          <div>Launch & Approve</div>
        </ModalTitle>
      }
      footer={
        <FooterContainer id="Psima_Dashboard_QuickLaunch_Modal_Footer">
          <Button
            id="Psima_Dashboard_QuickLaunch_Cancel_Button"
            onClick={() => setIsQuickLaunchModalOpen(false)}
            size={SIZES.MEDIUM}
            variant={VARIANTS.SECONDARY}
          >
            Cancel
          </Button>
          <Button
            id="Psima_Dashboard_QuickLaunch_Generate_Button"
            dataPendoFeature={PSIMA_PENDO_FEATURES.DASHBOARD_QUICK_LAUNCH_GENERATE}
            leftElement={
              <Rocket width="16px" height="16px" fill="var(--button-primary-text-default)" />
            }
            onClick={onGenerateClick}
            loading={launchLoader}
            size={SIZES.MEDIUM}
          >
            Launch
          </Button>
        </FooterContainer>
      }
      onClose={() => setIsQuickLaunchModalOpen(false)}
    >
      <Content>
        <StatsContainer>
          <StatsItem>
            <IconBox>
              <SmsTracking
                width="18px"
                height="18px"
                fill="var(--psima-quick-launch-modal-icon-color)"
              />
            </IconBox>
            <StatsText>
              <StatsCount data-testid="Psima_Dashboard_QuickLaunch_Modal_CampaignsCount_span">
                {campaignsCount}
              </StatsCount>
              <StatsLabel>{campaignsCount === 1 ? 'Simulation' : 'Simulations'}</StatsLabel>
            </StatsText>
          </StatsItem>
          <StatsItem data-testid="Psima_Dashboard_QuickLaunch_Modal_ParticipantsCount_div">
            <IconBox>
              <Users width="18px" height="18px" fill="var(--psima-quick-launch-modal-icon-color)" />
            </IconBox>
            <StatsText>
              <StatsCount data-testid="Psima_Dashboard_QuickLaunch_Modal_ParticipantsCount_span">
                {participantsCount}
              </StatsCount>
              <StatsLabel>{participantsCount === 1 ? 'Employee' : 'Employees'}</StatsLabel>
            </StatsText>
          </StatsItem>
        </StatsContainer>
        <Description>
          Your Phishing Simulation Agent will now deliver automatically personalized emails to all
          selected participants.
        </Description>
        <InfoRow>
          <IconBox>
            <Language
              width="18px"
              height="18px"
              fill="var(--psima-quick-launch-modal-icon-color)"
            />
          </IconBox>
          <div>
            <InfoTitle>Emails are localized automatically</InfoTitle>
            {/* <StatsLabel>
              Recipient&apos;s primary language will be used where it strengthens the lure - English
              otherwise. You&apos;ll see the language sent after launch.
            </StatsLabel> */}
          </div>
        </InfoRow>
      </Content>
    </Modal>
  )
}

export default QuickLaunchModal
